import { Link } from '@inertiajs/react'
import { Calendar, Edit2 } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { buttonClasses } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'

type TrainingContent = {
  id: string
  name: string
}

type DiaryTheme = {
  id: string
  name: string
}

type Diary = {
  id: string
  diaryDate: string
  activityType: string
  activityTypeLabel: string
  concentrationRating: number | null
  attitudeRating: number | null
  understandingRating: number | null
  challengeRating: number | null
  continuityRating: number | null
  reflection: string | null
  trainingContents: TrainingContent[]
  diaryThemes: DiaryTheme[]
  createdAt: string
}

type Props = {
  diary: Diary
  breadcrumbs: Breadcrumb[]
}

export default function Show({ diary, breadcrumbs }: Props) {
  const date = new Date(diary.diaryDate)
  const formattedDate = `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`
  const weekday = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()]

  return (
    <AppShell>
      <PageHeader
        title={`${formattedDate}（${weekday}）の日記`}
        breadcrumbs={breadcrumbs}
        actions={
          <Link
            href={`/player/soccer_diaries/${diary.id}/edit`}
            className={buttonClasses({ variant: 'secondary' })}
          >
            <Edit2 className="mr-2 h-4 w-4" />
            編集
          </Link>
        }
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,2fr)_1fr]">
        <div className="space-y-6">
          <Card className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-500/20 text-brand-300">
                <Calendar className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">
                  {formattedDate}（{weekday}）
                </h2>
                <Badge variant={activityTypeBadgeVariant(diary.activityType)}>
                  {diary.activityTypeLabel}
                </Badge>
              </div>
            </div>
          </Card>

          {diary.trainingContents.length > 0 && (
            <Card className="space-y-4">
              <h3 className="font-semibold text-white">練習内容</h3>
              <div className="flex flex-wrap gap-2">
                {diary.trainingContents.map((tc) => (
                  <span
                    key={tc.id}
                    className="rounded-full bg-brand-500/20 px-3 py-1 text-sm text-brand-200"
                  >
                    {tc.name}
                  </span>
                ))}
              </div>
            </Card>
          )}

          {diary.diaryThemes.length > 0 && (
            <Card className="space-y-4">
              <h3 className="font-semibold text-white">今日のテーマ</h3>
              <div className="flex flex-wrap gap-2">
                {diary.diaryThemes.map((dt) => (
                  <span
                    key={dt.id}
                    className="rounded-full bg-white/10 px-3 py-1 text-sm text-white/80"
                  >
                    {dt.name}
                  </span>
                ))}
              </div>
            </Card>
          )}

          {diary.reflection && (
            <Card className="space-y-4">
              <h3 className="font-semibold text-white">気づき・振り返り</h3>
              <p className="whitespace-pre-wrap text-white/80">{diary.reflection}</p>
            </Card>
          )}
        </div>

        <aside className="space-y-6">
          <Card className="space-y-4">
            <h3 className="font-semibold text-white">自己評価</h3>
            <div className="space-y-3">
              <RatingDisplay label="集中力" rating={diary.concentrationRating} />
              <RatingDisplay label="姿勢" rating={diary.attitudeRating} />
              <RatingDisplay label="理解度" rating={diary.understandingRating} />
              <RatingDisplay label="チャレンジ" rating={diary.challengeRating} />
              <RatingDisplay label="継続力" rating={diary.continuityRating} />
            </div>
          </Card>

          <Card variant="muted" className="space-y-2">
            <p className="text-xs text-white/50">作成日時</p>
            <p className="text-sm text-white/70">{diary.createdAt}</p>
          </Card>
        </aside>
      </div>
    </AppShell>
  )
}

function RatingDisplay({ label, rating }: { label: string; rating: number | null }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-white/70">{label}</span>
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={rating && star <= rating ? 'text-yellow-400' : 'text-white/20'}
          >
            ★
          </span>
        ))}
      </div>
    </div>
  )
}

function activityTypeBadgeVariant(activityType: string) {
  switch (activityType) {
    case 'practice':
      return 'info' as const
    case 'match':
      return 'success' as const
    case 'self_training':
      return 'warning' as const
    default:
      return 'outline' as const
  }
}
